import { buildApiUrl, getAuthHeaders } from "../config/api";

export interface PersonaDefinition {
  type: string;
  name: string;
  description: string;
  focusAreas: string[];
  expertise: string;
}

export interface UserPersonaPreferences {
  selectedPersonas: string[];
}

class PersonaService {
  // Get all available persona definitions
  async getPersonaDefinitions(): Promise<PersonaDefinition[]> {
    try {
      const response = await fetch(buildApiUrl("/personas/definitions"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error("Error fetching persona definitions:", error);
      throw error;
    }
  }

  // Get user's selected personas
  async getUserSelectedPersonas(): Promise<string[]> {
    try {
      const response = await fetch(buildApiUrl("/user/selected-personas"), {
        method: "GET",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data.selectedPersonas;
    } catch (error) {
      console.error("Error fetching user selected personas:", error);
      throw error;
    }
  }

  // Update user's selected personas
  async updateUserSelectedPersonas(selectedPersonas: string[]): Promise<void> {
    try {
      const response = await fetch(buildApiUrl("/user/selected-personas"), {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ selectedPersonas }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || "Failed to update selected personas");
      }
    } catch (error) {
      console.error("Error updating user selected personas:", error);
      throw error;
    }
  }

  // Get only the persona definitions that the user has selected in settings
  async getUserSelectedPersonaDefinitions(): Promise<PersonaDefinition[]> {
    try {
      // Get all persona definitions
      const allPersonas = await this.getPersonaDefinitions();

      // Get user's selected persona types
      const selectedPersonaTypes = await this.getUserSelectedPersonas();

      // Filter to only include selected personas
      return allPersonas.filter(persona =>
        selectedPersonaTypes.includes(persona.type)
      );
    } catch (error) {
      console.error("Error fetching user selected persona definitions:", error);
      throw error;
    }
  }

  // Get persona display name for UI
  getPersonaDisplayName(type: string): string {
    const displayNames: Record<string, string> = {
      CORPORATE_SECRETARIAL: "Corporate & Secretarial",
      FINANCIAL_INDEBTEDNESS: "Financial Indebtedness",
      PROJECTS: "Projects",
      MATERIAL_CONTRACTS: "Material Contracts",
      ASSETS: "Assets",
      MATERIAL_APPROVALS_LICENSES: "Material Approvals & Licenses",
      HUMAN_RESOURCE: "Human Resource",
      INTELLECTUAL_PROPERTY_RIGHTS: "Intellectual Property Rights",
      INSURANCES: "Insurances",
      LITIGATIONS_DISPUTES: "Litigations & Disputes",
    };
    return displayNames[type] || type;
  }

  // Get persona icon class for UI
  getPersonaIcon(type: string): string {
    const iconClasses: Record<string, string> = {
      CORPORATE_SECRETARIAL: "text-blue-600",
      FINANCIAL_INDEBTEDNESS: "text-green-600",
      PROJECTS: "text-purple-600",
      MATERIAL_CONTRACTS: "text-orange-600",
      ASSETS: "text-cyan-600",
      MATERIAL_APPROVALS_LICENSES: "text-red-600",
      HUMAN_RESOURCE: "text-pink-600",
      INTELLECTUAL_PROPERTY_RIGHTS: "text-violet-600",
      INSURANCES: "text-emerald-600",
      LITIGATIONS_DISPUTES: "text-slate-600",
    };
    return iconClasses[type] || "text-gray-600";
  }
}

export const personaService = new PersonaService();
